import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Row, Col, Accordion, FloatingLabel, InputGroup, OverlayTrigger, Tooltip, Badge } from 'react-bootstrap';
import { useNavigate, Link, useParams } from 'react-router-dom';
import api from '../services/api';
import FeedbackModal from '../components/FeedbackModal';
import { FiArrowLeft, FiSave, FiXCircle, FiInfo, FiCalendar, FiMapPin, FiClock, FiGlobe, FiType, FiAlignLeft, FiCheckCircle } from 'react-icons/fi';

const CreateTraining = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    
    // Split date and time in state
    const [formData, setFormData] = useState({
        titulo: '',
        descricao: '',
        date: '',
        time: '',
        local: '',
        sala_id: '',
        duracao: '',
        link_online: '',
        obriga_checkout: false,
        double_check: false,
        tolerance_checkin: 15,
        tolerance_checkout: 15
    });
    
    const [rooms, setRooms] = useState([]);
    const [touched, setTouched] = useState({});
    const [feedback, setFeedback] = useState({ type: '', text: '', title: '' });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchRooms = async () => {
            try {
                const response = await api.get('/rooms');
                setRooms(response.data.filter(r => r.ativa));
            } catch (error) {
                console.error('Erro ao buscar salas', error);
            }
        };
        fetchRooms();
    }, []);

    useEffect(() => {
        if (id) {
            const fetchTraining = async () => {
                try {
                    setLoading(true);
                    const response = await api.get(`/trainings/${id}`);
                    const data = response.data;
                    
                    const dateObj = new Date(data.data);
                    const year = dateObj.getFullYear();
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    const dateStr = `${year}-${month}-${day}`;
                    
                    const hours = String(dateObj.getHours()).padStart(2, '0');
                    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
                    const timeStr = `${hours}:${minutes}`;

                    setFormData({
                        titulo: data.titulo,
                        descricao: data.descricao || '',
                        date: dateStr,
                        time: timeStr,
                        local: data.local || '',
                        sala_id: data.sala_id || '',
                        duracao: data.duracao,
                        link_online: data.link_online || '',
                        obriga_checkout: data.obriga_checkout !== undefined ? data.obriga_checkout : false,
                        double_check: data.double_check !== undefined ? data.double_check : false,
                        tolerance_checkin: data.tolerance_checkin !== undefined ? data.tolerance_checkin : 15,
                        tolerance_checkout: data.tolerance_checkout !== undefined ? data.tolerance_checkout : 15
                    });
                } catch (err) {
                    console.error('Erro ao carregar treinamento', err);
                    setFeedback({ type: 'danger', text: 'Erro ao carregar dados do treinamento.' });
                } finally {
                    setLoading(false);
                }
            };
            fetchTraining();
        }
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Clear specific error when user types (optional, but good UX)
        if (feedback.type === 'danger') setFeedback({ type: '', text: '' });
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
    };

    const isFieldValid = (name) => {
        if (!touched[name]) return null; // Not validated yet
        return formData[name] && formData[name].toString().trim() !== '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setFeedback({ type: '', text: '' });

        // Basic validation
        if (!formData.titulo || !formData.date || !formData.time || !formData.duracao) {
            setFeedback({ type: 'danger', text: 'Por favor, preencha todos os campos obrigatórios.' });
            setLoading(false);
            setTouched({
                titulo: true,
                date: true,
                time: true,
                duracao: true
            });
            return;
        }

        try {
            // Combine date and time to ISO format
            const dateTime = `${formData.date}T${formData.time}:00`;

            const payload = {
                titulo: formData.titulo,
                descricao: formData.descricao,
                data: dateTime,
                local: formData.local,
                sala_id: formData.sala_id || null,
                duracao: formData.duracao,
                link_online: formData.link_online,
                obriga_checkout: formData.obriga_checkout,
                double_check: formData.double_check,
                tolerance_checkin: formData.tolerance_checkin,
                tolerance_checkout: formData.tolerance_checkout,
                participantes: []
            };

            if (id) {
                await api.put(`/trainings/${id}`, payload);
                setFeedback({ type: 'success', text: 'Treinamento atualizado com sucesso!', title: 'Sucesso' });
                setTimeout(() => navigate('/training-list'), 1500);
            } else {
                await api.post('/trainings', payload);
                setFeedback({ type: 'success', text: 'Treinamento criado com sucesso!', title: 'Sucesso' });
                setTimeout(() => navigate('/training-list'), 1500);
            }
        } catch (err) {
            console.error(err);
            const msg = err.response?.data?.msg || 'Erro ao salvar treinamento. Tente novamente.';
            // Check for conflict specific message
            const title = err.response?.status === 409 ? 'Conflito de Horário' : 'Erro';
            setFeedback({ type: 'danger', text: msg, title: title });
            setLoading(false);
        }
    };

    // Calculate end time for summary
    const getEndTime = () => {
        if (!formData.time || !formData.duracao) return '';
        const [hours, minutes] = formData.time.split(':').map(Number);
        const date = new Date();
        date.setHours(hours, minutes);
        date.setHours(date.getHours() + parseInt(formData.duracao));
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <Container className="mt-4 mb-5">
            <div className="mb-4">
                <Button 
                    variant="link" 
                    as={Link} 
                    to="/training-list" 
                    className="text-decoration-none p-0 mb-2 text-muted"
                >
                    <FiArrowLeft className="me-1" /> Voltar para Lista de treinamento
                </Button>
                <h2 className="fw-bold text-dark">{id ? 'Editar Treinamento' : 'Novo Treinamento'}</h2>
                <p className="text-muted">{id ? 'Atualize os dados do treinamento.' : 'Preencha os dados abaixo para agendar um novo treinamento.'}</p>
            </div>

            <FeedbackModal 
                show={!!feedback.text} 
                onHide={() => setFeedback({ ...feedback, text: '' })} 
                type={feedback.type} 
                title={feedback.title}
                message={feedback.text} 
            />

            <Row>
                {/* Left Column: Form */}
                <Col lg={8}>
                    <Form onSubmit={handleSubmit} noValidate>
                        <Card className="shadow-sm border-0 mb-4">
                            <Card.Body className="p-4">
                                <h5 className="mb-4 text-primary fw-bold"><FiType className="me-2" />Informações Básicas</h5>
                                <Row className="g-3">
                                    <Col md={12}>
                                        <Form.Group controlId="formTitulo">
                                            <FloatingLabel controlId="floatingTitulo" label="Título do Treinamento *">
                                                <Form.Control 
                                                    type="text" 
                                                    name="titulo"
                                                    value={formData.titulo} 
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Título"
                                                    isValid={touched.titulo && isFieldValid('titulo')}
                                                    isInvalid={touched.titulo && !isFieldValid('titulo')}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    O título é obrigatório.
                                                </Form.Control.Feedback>
                                            </FloatingLabel>
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group controlId="formDescricao">
                                            <FloatingLabel controlId="floatingDescricao" label="Descrição (Opcional)">
                                                <Form.Control 
                                                    as="textarea" 
                                                    name="descricao"
                                                    value={formData.descricao} 
                                                    onChange={handleChange}
                                                    placeholder="Descrição"
                                                    style={{ height: '100px' }}
                                                />
                                            </FloatingLabel>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <Accordion defaultActiveKey="0" className="shadow-sm border-0 mb-4 rounded overflow-hidden">
                            <Accordion.Item eventKey="0" className="border-0">
                                <Accordion.Header><strong className="text-primary"><FiCalendar className="me-2" />Detalhes do Treinamento</strong></Accordion.Header>
                                <Accordion.Body className="p-4 bg-white">
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group controlId="formData">
                                                <Form.Label className="small text-muted fw-bold">DATA *</Form.Label>
                                                <InputGroup hasValidation>
                                                    <InputGroup.Text className="bg-light border-end-0"><FiCalendar /></InputGroup.Text>
                                                    <Form.Control 
                                                        type="date" 
                                                        name="date"
                                                        value={formData.date} 
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        className="border-start-0 ps-0"
                                                        isValid={touched.date && isFieldValid('date')}
                                                        isInvalid={touched.date && !isFieldValid('date')}
                                                    />
                                                    <Form.Control.Feedback type="invalid">Selecione uma data.</Form.Control.Feedback>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group controlId="formTime">
                                                <Form.Label className="small text-muted fw-bold">HORÁRIO *</Form.Label>
                                                <InputGroup hasValidation>
                                                    <InputGroup.Text className="bg-light border-end-0"><FiClock /></InputGroup.Text>
                                                    <Form.Control 
                                                        type="time" 
                                                        name="time"
                                                        value={formData.time} 
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        className="border-start-0 ps-0"
                                                        isValid={touched.time && isFieldValid('time')}
                                                        isInvalid={touched.time && !isFieldValid('time')}
                                                    />
                                                    <Form.Control.Feedback type="invalid">Obrigatório.</Form.Control.Feedback>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group controlId="formDuracao">
                                                <Form.Label className="small text-muted fw-bold">DURAÇÃO (H) *</Form.Label>
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip>Duração estimada em horas para cálculo de término.</Tooltip>}
                                                >
                                                    <InputGroup hasValidation>
                                                        <Form.Control 
                                                            type="number" 
                                                            min="1"
                                                            name="duracao"
                                                            value={formData.duracao} 
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            placeholder="Ex: 2"
                                                            isValid={touched.duracao && isFieldValid('duracao')}
                                                            isInvalid={touched.duracao && !isFieldValid('duracao')}
                                                        />
                                                        <InputGroup.Text>h</InputGroup.Text>
                                                        <Form.Control.Feedback type="invalid">Inválido.</Form.Control.Feedback>
                                                    </InputGroup>
                                                </OverlayTrigger>
                                            </Form.Group>
                                        </Col>
                                        
                                        <Col md={12}>
                                            <hr className="my-2 text-muted opacity-25"/>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group controlId="formSala">
                                                <Form.Label className="small text-muted fw-bold">VINCULAR SALA (MODO TABLET)</Form.Label>
                                                <Form.Select 
                                                    name="sala_id" 
                                                    value={formData.sala_id} 
                                                    onChange={(e) => {
                                                        const salaId = e.target.value;
                                                        const sala = rooms.find(r => r.id === parseInt(salaId));
                                                        setFormData(prev => ({ 
                                                            ...prev, 
                                                            sala_id: salaId,
                                                            local: sala ? sala.nome : prev.local 
                                                        }));
                                                    }}
                                                >
                                                    <option value="">Nenhuma (Apenas Texto)</option>
                                                    {rooms.map(room => (
                                                        <option key={room.id} value={room.id}>{room.nome} (Cap: {room.capacidade})</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group controlId="formLocal">
                                                <Form.Label className="small text-muted fw-bold">LOCAL PRESENCIAL</Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text className="bg-light border-end-0"><FiMapPin /></InputGroup.Text>
                                                    <Form.Control 
                                                        type="text" 
                                                        name="local"
                                                        value={formData.local} 
                                                        onChange={handleChange}
                                                        placeholder="Ex: Sala de Reuniões"
                                                        className="border-start-0 ps-0"
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                        
                                        <Col md={6}>
                                            <Form.Group controlId="formLink">
                                                <Form.Label className="small text-muted fw-bold">LINK PARA TRANSMISSÃO ONLINE</Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text className="bg-light border-end-0">https://</InputGroup.Text>
                                                    <Form.Control 
                                                        type="url" 
                                                        name="link_online"
                                                        value={formData.link_online} 
                                                        onChange={handleChange}
                                                        placeholder="meet.google.com/abc-defg-hij"
                                                        className="border-start-0 ps-0"
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Accordion.Body>
                            </Accordion.Item>
                            
                            <Accordion.Item eventKey="1" className="border-0 border-top">
                                <Accordion.Header><strong className="text-secondary"><FiGlobe className="me-2" />Configurações Avançadas</strong></Accordion.Header>
                                <Accordion.Body className="p-4 bg-white">
                                    <Row className="g-3">
                                        <Col md={12}>
                                            <Form.Group controlId="formObrigaCheckout">
                                                <Form.Check 
                                                    type="switch"
                                                    id="obriga-checkout-switch"
                                                    label="Obrigar check-out"
                                                    name="obriga_checkout"
                                                    checked={formData.obriga_checkout}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, obriga_checkout: e.target.checked }))}
                                                    className="fw-bold text-secondary"
                                                />
                                                <Form.Text className="text-muted">
                                                    Se ativado, o participante precisará realizar check-in e check-out para confirmar presença.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>

                                        <Col md={12}>
                                            <Form.Group controlId="formDoubleCheck">
                                                <Form.Check
                                                    type="switch"
                                                    id="double-check-switch"
                                                    label="Double-check"
                                                    name="double_check"
                                                    checked={formData.double_check}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, double_check: e.target.checked }))}
                                                    className="fw-bold text-secondary"
                                                />
                                                <Form.Text className="text-muted">
                                                    Se ativado, o CPF será obrigatório no check-in/check-out. Se desativado, CPF será opcional.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>

                                        <Col md={12}>
                                            <hr className="my-2 text-muted opacity-25"/>
                                            <h6 className="text-primary fw-bold small mb-3"><FiClock className="me-2"/>Tolerância de Horário (Minutos)</h6>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group controlId="formToleranceCheckin">
                                                <Form.Label className="small text-muted fw-bold">TOLERÂNCIA CHECK-IN</Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text className="bg-light border-end-0"><FiClock /></InputGroup.Text>
                                                    <Form.Control 
                                                        type="number" 
                                                        min="0"
                                                        name="tolerance_checkin"
                                                        value={formData.tolerance_checkin} 
                                                        onChange={handleChange}
                                                        className="border-start-0 ps-0"
                                                    />
                                                    <InputGroup.Text>min</InputGroup.Text>
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Tempo extra permitido antes/depois do início.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>

                                        {formData.obriga_checkout && (
                                            <Col md={6}>
                                                <Form.Group controlId="formToleranceCheckout">
                                                    <Form.Label className="small text-muted fw-bold">TOLERÂNCIA CHECK-OUT</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text className="bg-light border-end-0"><FiClock /></InputGroup.Text>
                                                        <Form.Control 
                                                            type="number" 
                                                            min="0"
                                                            name="tolerance_checkout"
                                                            value={formData.tolerance_checkout} 
                                                            onChange={handleChange}
                                                            className="border-start-0 ps-0"
                                                        />
                                                        <InputGroup.Text>min</InputGroup.Text>
                                                    </InputGroup>
                                                    <Form.Text className="text-muted">
                                                        Tempo extra permitido antes/depois do fim.
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        )}
                                    </Row>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>

                        <div className="d-flex justify-content-end gap-2 mb-5">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/training-list')}
                                disabled={loading}
                                className="px-4"
                            >
                                <FiXCircle className="me-2" /> Cancelar
                            </Button>
                            <Button 
                                variant="primary" 
                                type="submit" 
                                disabled={loading}
                                className="px-4 fw-bold"
                            >
                                {loading ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Salvando...</> : <><FiSave className="me-2" /> Salvar Treinamento</>}
                            </Button>
                        </div>
                    </Form>
                </Col>

                {/* Right Column: Visual Summary */}
                <Col lg={4}>
                    <div className="sticky-top" style={{ top: '20px', zIndex: 1 }}>
                        <Card className="shadow-sm border-0 bg-light">
                            <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
                                <h6 className="text-uppercase text-muted fw-bold small ls-1">Resumo do Treinamento</h6>
                            </Card.Header>
                            <Card.Body>
                                <div className="mb-3">
                                    <h4 className="fw-bold text-dark mb-1">
                                        {formData.titulo || <span className="text-muted fst-italic">Sem título definido</span>}
                                    </h4>
                                    {formData.descricao && (
                                        <p className="text-muted small mt-2 line-clamp-3">
                                            {formData.descricao}
                                        </p>
                                    )}
                                </div>
                                
                                <div className="d-flex flex-column gap-3 mt-4">
                                    <div className="d-flex align-items-center text-secondary">
                                        <div className="bg-white p-2 rounded shadow-sm me-3 text-primary">
                                            <FiCalendar size={20} />
                                        </div>
                                        <div>
                                            <small className="d-block text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>Data</small>
                                            <strong>{formData.date ? new Date(formData.date).toLocaleDateString('pt-BR') : '-'}</strong>
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-center text-secondary">
                                        <div className="bg-white p-2 rounded shadow-sm me-3 text-primary">
                                            <FiClock size={20} />
                                        </div>
                                        <div>
                                            <small className="d-block text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>Horário</small>
                                            <strong>
                                                {formData.time || '-'} 
                                                {formData.time && formData.duracao && ` - ${getEndTime()}`}
                                            </strong>
                                        </div>
                                    </div>

                                    {(formData.local || formData.link_online) && (
                                        <div className="d-flex align-items-center text-secondary">
                                            <div className="bg-white p-2 rounded shadow-sm me-3 text-primary">
                                                {formData.link_online ? <FiGlobe size={20} /> : <FiMapPin size={20} />}
                                            </div>
                                            <div>
                                                <small className="d-block text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>Local</small>
                                                <div className="d-flex flex-column">
                                                    {formData.local && <span>{formData.local}</span>}
                                                    {formData.link_online && <Badge bg="info" className="mt-1 w-auto align-self-start">Online</Badge>}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="mt-4 pt-3 border-top">
                                    <div className="d-flex align-items-center justify-content-between text-success">
                                        <span className="small fw-bold"><FiCheckCircle className="me-1"/> Status</span>
                                        <Badge bg="success">Novo</Badge>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default CreateTraining;
